import type { LatLng, Map as LeafletMap } from 'leaflet';

const POPUP_MARGIN = 12;
const POPUP_GAP = 18;
const POPUP_MAX_WIDTH = 320;
const POPUP_MIN_WIDTH = 260;

type ScreenPoint = { x: number; y: number };

export type LocationPopupOptions = {
  root: HTMLElement;
  map: LeafletMap;
  mapEl: HTMLElement;
};

function prefersReducedMotion(): boolean {
  if (typeof globalThis.matchMedia !== 'function') {
    return false;
  }
  return globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function resolveMountDocument(): Document {
  try {
    if (window.self !== window.top && window.parent.document.body) {
      return window.parent.document;
    }
  } catch {
    // Cross-origin iframe — stay in current document.
  }
  return document;
}

function injectStylesInto(doc: Document): void {
  if (doc.querySelector('[data-kfx-map-popup-styles]')) {
    return;
  }
  for (const el of document.querySelectorAll('link[rel="stylesheet"]')) {
    const href = el.getAttribute('href');
    if (!href) {
      continue;
    }
    if (!href.includes('kfx-interactive-map') && !href.includes('/dist/assets/style-')) {
      continue;
    }
    const link = doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = new URL(href, document.baseURI).href;
    link.setAttribute('data-kfx-map-popup-styles', '');
    doc.head.appendChild(link);
    return;
  }
}

function markerScreenPoint(map: LeafletMap, mapEl: HTMLElement, latLng: LatLng): ScreenPoint {
  const point = map.latLngToContainerPoint(latLng);
  const mapRect = mapEl.getBoundingClientRect();
  let x = mapRect.left + point.x;
  let y = mapRect.top + point.y;

  try {
    if (window.self !== window.top && resolveMountDocument() !== document) {
      const frameEl = window.frameElement;
      if (frameEl) {
        const frameRect = frameEl.getBoundingClientRect();
        x = frameRect.left + mapRect.left + point.x;
        y = frameRect.top + mapRect.top + point.y;
      }
    }
  } catch {
    // Keep iframe-local coordinates.
  }

  return { x, y };
}

function viewportSize(doc: Document): { width: number; height: number } {
  return { width: doc.defaultView?.innerWidth ?? window.innerWidth, height: doc.defaultView?.innerHeight ?? window.innerHeight };
}

export class LocationPopup {
  private readonly mountDoc: Document;
  private readonly host: HTMLElement;
  private readonly shell: HTMLElement;
  private readonly contentHost: HTMLElement;
  private activeLatLng: LatLng | null = null;
  private docked = false;

  constructor(private readonly opts: LocationPopupOptions) {
    this.mountDoc = resolveMountDocument();
    if (this.mountDoc !== document) {
      injectStylesInto(this.mountDoc);
    }

    this.host = this.mountDoc.createElement('div');
    this.host.className = 'kfx-interactive-map--ready kfx-interactive-map__popup-host';

    this.shell = this.mountDoc.createElement('div');
    this.shell.className = 'kfx-interactive-map__popup-portal';
    this.shell.hidden = true;

    this.contentHost = this.mountDoc.createElement('div');
    this.contentHost.className = 'kfx-interactive-map__popup-content';
    this.shell.append(this.contentHost);

    this.host.append(this.shell);
    this.mountTarget().append(this.host);

    this.shell.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    this.mountDoc.addEventListener('keydown', this.onKeyDown);
    this.mountDoc.addEventListener('click', this.onOutsideClick, true);
    if (this.mountDoc !== document) {
      document.addEventListener('click', this.onOutsideClick, true);
    }
    window.addEventListener('resize', this.reposition, { passive: true });
    opts.map.on('move', this.reposition);
    opts.map.on('zoom', this.reposition);
  }

  open(content: HTMLElement, latLng: LatLng): void {
    this.contentHost.replaceChildren(content);
    this.bindCloseButton(content);
    this.syncDialogLabel(content);

    this.activeLatLng = latLng;
    this.shell.hidden = false;
    requestAnimationFrame(() => {
      this.reposition();
    });

    const closeBtn = content.querySelector<HTMLButtonElement>('.kfx-map-popup__close');
    closeBtn?.focus({ preventScroll: true });
  }

  close(): void {
    if (this.shell.hidden) {
      return;
    }
    this.shell.hidden = true;
    this.contentHost.replaceChildren();
    this.activeLatLng = null;
    this.setDocked(false);
  }

  isOpen(): boolean {
    return !this.shell.hidden;
  }

  destroy(): void {
    this.close();
    this.mountDoc.removeEventListener('keydown', this.onKeyDown);
    this.mountDoc.removeEventListener('click', this.onOutsideClick, true);
    if (this.mountDoc !== document) {
      document.removeEventListener('click', this.onOutsideClick, true);
    }
    window.removeEventListener('resize', this.reposition);
    this.opts.map.off('move', this.reposition);
    this.opts.map.off('zoom', this.reposition);
    this.host.remove();
  }

  private onOutsideClick = (event: MouseEvent): void => {
    if (this.shell.hidden) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }
    if (this.host.contains(target) || this.opts.root.contains(target)) {
      return;
    }
    this.close();
  };

  private mountTarget(): HTMLElement {
    return this.mountDoc.body;
  }

  private bindCloseButton(content: HTMLElement): void {
    content.querySelector<HTMLButtonElement>('.kfx-map-popup__close')?.addEventListener('click', (event) => {
      event.stopPropagation();
      event.preventDefault();
      this.close();
    });
  }

  private syncDialogLabel(content: HTMLElement): void {
    const title = content.querySelector<HTMLElement>('.kfx-map-popup__title, .kfx-map-accordion__summary');
    if (!title) {
      this.shell.removeAttribute('role');
      this.shell.removeAttribute('aria-labelledby');
      return;
    }
    if (!title.id) {
      title.id = `kfx-map-popup-title-${Date.now().toString(36)}`;
    }
    this.shell.setAttribute('role', 'dialog');
    this.shell.setAttribute('aria-labelledby', title.id);
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || this.shell.hidden) {
      return;
    }
    event.preventDefault();
    this.close();
  };

  private reposition = (): void => {
    if (this.shell.hidden || !this.activeLatLng) {
      return;
    }

    this.shell.style.visibility = 'hidden';
    this.shell.style.left = '0';
    this.shell.style.top = '0';
    this.setDocked(false);

    const marker = markerScreenPoint(this.opts.map, this.opts.mapEl, this.activeLatLng);
    const popupRect = this.shell.getBoundingClientRect();
    const { width: vw, height: vh } = viewportSize(this.mountDoc);

    const width = Math.min(POPUP_MAX_WIDTH, Math.max(POPUP_MIN_WIDTH, popupRect.width || POPUP_MAX_WIDTH));
    let left = marker.x - width / 2;
    left = Math.max(POPUP_MARGIN, Math.min(left, vw - width - POPUP_MARGIN));

    const spaceAbove = marker.y - POPUP_GAP - POPUP_MARGIN;
    const spaceBelow = vh - marker.y - POPUP_GAP - POPUP_MARGIN;
    const popupHeight = popupRect.height || 240;

    let top = marker.y - popupHeight - POPUP_GAP;
    if (top < POPUP_MARGIN || (spaceAbove < popupHeight && spaceBelow > spaceAbove)) {
      top = marker.y + POPUP_GAP;
    }

    const fitsInViewport = top >= POPUP_MARGIN && top + popupHeight <= vh - POPUP_MARGIN;
    if (!fitsInViewport) {
      this.setDocked(true);
      this.shell.style.visibility = '';
      return;
    }

    this.shell.style.position = 'fixed';
    this.shell.style.width = `${width}px`;
    this.shell.style.maxWidth = `min(${POPUP_MAX_WIDTH}px, calc(100vw - ${POPUP_MARGIN * 2}px))`;
    this.shell.style.left = `${left}px`;
    this.shell.style.top = `${top}px`;
    this.shell.style.visibility = '';
    this.shell.style.transform = prefersReducedMotion() ? '' : 'translateZ(0)';
  };

  private setDocked(next: boolean): void {
    if (this.docked === next) {
      if (next) {
        this.opts.root.append(this.host);
      }
      return;
    }
    this.docked = next;
    this.shell.classList.toggle('kfx-interactive-map__popup-portal--docked', next);
    if (next) {
      this.shell.style.position = '';
      this.shell.style.left = '';
      this.shell.style.top = '';
      this.shell.style.width = '';
      this.shell.style.maxWidth = '';
      this.shell.style.transform = '';
      this.opts.root.append(this.host);
      return;
    }
    this.mountTarget().append(this.host);
  }
}
